import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  Button,
  Grid,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  RadioGroup,
  IconButton,
  Radio,
  Switch,
  Divider,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import DoneIcon from '@mui/icons-material/Done';
import { ExpandMore, Description, Info } from '@mui/icons-material';
import { normalizeText } from '../../utils/utils';
import "./LoyaltyManagement.css";
import { ActiveModule, TaskFormData } from './utils';
import Header from "../../components/dms_common/Header";
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { openPopup } from "../../stateManagement/actions/popupActions";
import OutletFilters from '../manageCoupons/components/DefineCoupon/MarketDetails/OutletFilters';
import { combinedSelectedValuesType } from '../manageCoupons/types';
import { DB_COLUMN_NAME_MAP_COLUMN_NAME } from '../manageCoupons/utils/utils';
import { generateRandomId, getEntityPriority } from '../manageCoupons/services/services';
import { getMarketDetailsOptions } from '../manageCoupons/components/DefineCoupon/MarketDetails/service';
import { getClientConfiguartions } from '../../services/updateTransaltionService';
import { createTask, updateTask, createCampaign } from "../../services/loyaltyService";
import { uploadDocument } from "../../services/bankingService";
import { formatDecimal } from '../../components/dms_common/DmsUtils';
import SelectedOutletDetails from './SelectedOutletDetails';

const accordionStyle = {
  borderRadius: '12px',
  '&:before': {
    display: 'none',
  },
  boxShadow: 'none',
  border: '1px solid #e0e0e0',
  marginBottom: '16px',
  overflow: 'hidden',
  "& .MuiAccordionSummary-root": {
    backgroundColor: "#FFFFFF !important"
  },
  '&.Mui-expanded': {
    borderRadius: '12px',
  },
  '&:not(.Mui-expanded)': {
    borderRadius: '12px',
  }
};

const accordionSummaryStyle = {
  borderRadius: '12px 12px 0 0',
  '&.Mui-expanded': {
    minHeight: '30px',
    margin: 0,
    borderRadius: '12px 12px 0 0',
  },
  '& .MuiAccordionSummary-content': {
    margin: '12px 0',
    '&.Mui-expanded': {
      margin: '12px 0',
    }
  }
};

const CreateTask: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState: any = location.state as any;
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [activeRuleTab, setActiveRuleTab] = useState(0);
  const [outletLoading, setOutletLoading] = useState<boolean>(false);
  const [taskData, setTaskData] = useState<TaskFormData>(
    locationState?.taskData || {
      taskName: '',
      category: '',
      activeStatus: 'ACTIVE',
      template: '',
      description: '',
      thresholdValue: '',
      pointsReward: '',
      redirectionPlaceholder: '',
      redirection: null,
      startDate: '',
      endDate: '',
      applyToAll: false,
      outletBifurcationsList: [],
      guidanceType: 'written',
      guidanceContent: '',
      bannerLink: ''
    });
  const [isEditMode, setIsEditMode] = useState<boolean>(locationState?.isEditMode || false);
  const [isCloneMode, setIsCloneMode] = useState<boolean>(locationState?.isCloneMode || false);
  const [currency, setCurrency] = useState<string>('');
  const [outletCurrentTab, setOutletCurrentTab] = useState<number>(0);
  const [activeModules, setActiveModules] = useState<ActiveModule[]>([]);
  const [redemptionConfig, setRedemptionConfig] = useState<any>(null);
  const [navigationOptions, setNavigationOptions] = useState<any[]>([]);
  const [selectedRedirection, setSelectedRedirection] = useState<any>(null);
  const [iredScene, setIredScene] = useState<any>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    taskSetup: true,
    thresholds: false,
    assignment: false,
    guidance: false
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [bannerLink, setBannerLink] = useState<string>('');
  const [outletValues, setOutletValues] = useState<{ schemeOutletBifurcations: Record<string, string[]> }>({ schemeOutletBifurcations: {} });
  const [outletBifurcationsList, setOutletBifurcationsList] = useState<any[]>([]);
  const [editId, setEditId] = useState<string | null>(null);
  const editRef = useRef<number | null>(null);
  const ACCEPTED_FILE_TYPES = "image/png, image/jpeg";
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const outletLevelTabsList = [
    "distributorDetails",
    "channel",
    "outletClass",
    "outletCode",
  ];
  const DATE_FIELDS = ['startDate', 'endDate'];

  const getTaskStatus = (): 'not-started' | 'active' | 'expired' => {
    const originalStartDate = locationState?.taskData?.startDate;
    const originalEndDate = locationState?.taskData?.endDate;
    if (!originalStartDate) return 'not-started';

    const now = dayjs();
    const originalStart = dayjs(originalStartDate);
    const originalEnd = dayjs(originalEndDate).endOf('day');

    if (now.isBefore(originalStart)) {
      return 'not-started';
    } else if (now.isAfter(originalEnd)) {
      return 'expired';
    } else {
      return 'active';
    }
  };

  const isFieldDisabled = (fieldName: string): boolean => {
    if (isEditMode) {
      if (!DATE_FIELDS.includes(fieldName)) {
        return true;
      }

      const taskStatus = getTaskStatus();

      if (fieldName === 'startDate') {
        return taskStatus !== 'not-started';
      }

      switch (taskStatus) {
        case 'not-started':
          return false;
        case 'active':
          return fieldName === 'startDate';
        case 'expired':
          return true;
        default:
          return false;
      }
    }
    return false;
  };

  const isTaskExpired = (): boolean => {
    if (!taskData.endDate) return false;
    return dayjs().isAfter(dayjs(taskData.endDate).endOf('day'));
  };

  const handleOutletChange = (
    name: string,
    newValue: Record<string, string[]>
  ) => {
    setOutletValues((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleOutletChangeWrapper = useCallback(
    (name: string, newValue: Record<string, string | string[]>) => {
      const converted: Record<string, string[]> = {};
      for (const [key, value] of Object.entries(newValue)) {
        if (Array.isArray(value)) {
          converted[key] = value;
        } else if (typeof value === 'string') {
          converted[key] = value
            ? value
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
            : [];
        } else {
          converted[key] = [];
        }
      }
      handleOutletChange(name, converted);
    },
    []
  );

  const resetOutletValues = () => {
    setOutletValues({ schemeOutletBifurcations: {} });
    setEditId(null);
    editRef.current = null;
  };

  const handleSaveOutlet = async () => {
    const hasOutletSelection = Object.values(outletValues.schemeOutletBifurcations).some(
      (value) => Array.isArray(value) && value.length > 0
    );

    if (!hasOutletSelection) {
      openPopup("Alert", "Please select at least one outlet filter before saving.");
      return;
    }
    const newItem = {
      id: editId || generateRandomId(),
      ...outletValues.schemeOutletBifurcations,
    } as any;

    if (editId) {
      setOutletBifurcationsList((prev) =>
        prev.map((item) => (item.id === editId ? newItem : item))
      );
      setEditId(null);
    } else {
      setOutletBifurcationsList((prev) => [...prev, newItem]);
    }
    resetOutletValues();
  };

  const handleEditButtonClick = (rowIndex: number) => {
    if (editId !== null) {
      openPopup("Alert", "Another Row is already in edit mode. Please Save it first");
      return;
    }
    const item = outletBifurcationsList[rowIndex];
    const { id: _, ...rest } = item;
    setOutletValues({ schemeOutletBifurcations: rest });
    setEditId(item.id);
    editRef.current = rowIndex;
  };

  const handleRowDelete = (index: number) => {
    if (editId !== null) {
      openPopup("Alert", "Cannot perform Delete operation while editing");
      return;
    }
    setOutletBifurcationsList((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (editId !== null) return;
  }, [editId]);

  useEffect(() => {
    if (isCloneMode) {
      const updatedTaskData = { ...taskData };
      let shouldUpdate = false;

      if (isTaskExpired()) {
        updatedTaskData.startDate = '';
        updatedTaskData.endDate = '';
        shouldUpdate = true;
      }

      if (shouldUpdate) {
        setTaskData(updatedTaskData);
      }
    }
    if (isEditMode) {
      setExpandedSections(prev => ({
        ...prev,
        thresholds: true,
        taskSetup: false,
        assignment: false,
        guidance: false
      }));
    }
  }, [isEditMode, isCloneMode, taskData.startDate, taskData.endDate]);

  useEffect(() => {
    if ((isEditMode || isCloneMode) && taskData.outletBifurcationsList) {
      setOutletBifurcationsList(taskData.outletBifurcationsList);
    }
  }, [isEditMode, isCloneMode, taskData.outletBifurcationsList]);

  useEffect(() => {
    const fetchAllConfigs = async () => {
      try {
        const response = await getClientConfiguartions("customer_configuration");
        const features = response?.data?.features ?? [];
        if (features.length > 0) {
          const domainValues = features[0]?.domainValues ?? [];
          const currencyItem = domainValues.find((item: any) => item.name === "currencySymbol");
          if (currencyItem?.value) setCurrency(currencyItem.value);
        }

        const domainValues = locationState?.configData ?? [];
        const redemptionItem = domainValues.find((c: any) => c.name === "LoyaltyRedemption");
        const navigationItem = domainValues.find((c: any) => c.name === "NavigationOptions");
        const iredSceneItem = domainValues.find((c: any) => c.name === "IredScene");

        if (redemptionItem?.value?.length) setRedemptionConfig(redemptionItem.value[0]);
        if (navigationItem?.value) setNavigationOptions(navigationItem.value);
        if (iredSceneItem?.value) setIredScene(iredSceneItem.value[0]);

        if (locationState?.activeModules) {
          setActiveModules(locationState.activeModules);
        }
      } catch (error) {
        console.error("Error fetching configuration data:", error);
      }
    };

    fetchAllConfigs();
  }, [locationState?.configData, locationState?.activeModules]);

  useEffect(() => {
    if ((isEditMode || isCloneMode) && taskData?.redirection) {
      setSelectedRedirection(taskData.redirection);
    }

    if ((isEditMode || isCloneMode) && taskData?.bannerLink) {
      setBannerLink(taskData.bannerLink);
      if (taskData.bannerLink) {
        setPreviewUrl(taskData.bannerLink);
      }
    }
  }, [isEditMode, isCloneMode, taskData?.bannerLink, taskData?.redirection]);

  useEffect(() => {
    return () => {
      if (previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    if (isEditMode) return;

    const isTaskSetupComplete =
      taskData.taskName?.trim() &&
      taskData.category &&
      taskData.template &&
      taskData.description?.trim();

    if (expandedSections.taskSetup && isTaskSetupComplete && !expandedSections.thresholds) {
      setExpandedSections(prev => ({
        ...prev,
        thresholds: true
      }));
    }
  }, [taskData.taskName, taskData.category, taskData.template, taskData.description, expandedSections.taskSetup, isEditMode]);

  const hasRedirectionRoute = (): boolean => {
    const selectedModule = getSelectedModule();
    return !!selectedModule?.redirectionRoute;
  };

  useEffect(() => {
    if (isEditMode) return;

    const isThresholdsComplete = hasRedirectionRoute()
      ? taskData.thresholdValue &&
      taskData.pointsReward &&
      taskData.startDate &&
      taskData.endDate
      : taskData.thresholdValue &&
      taskData.pointsReward &&
      taskData.redirectionPlaceholder &&
      taskData.startDate &&
      taskData.endDate;

    if (expandedSections.thresholds && isThresholdsComplete && !expandedSections.assignment) {
      setExpandedSections(prev => ({
        ...prev,
        assignment: true
      }));
    }
  }, [
    taskData.thresholdValue,
    taskData.pointsReward,
    taskData.redirectionPlaceholder,
    taskData.startDate,
    taskData.endDate,
    expandedSections.thresholds,
    isEditMode,
    taskData.category
  ]);

  useEffect(() => {
    if (isEditMode) return;

    const hasOutletSelection = taskData.applyToAll || outletBifurcationsList.length > 0;

    if (expandedSections.assignment && hasOutletSelection && !expandedSections.guidance) {
      setExpandedSections(prev => ({
        ...prev,
        guidance: true
      }));
    }
  }, [taskData.applyToAll, outletBifurcationsList.length, expandedSections.assignment, isEditMode]);

  const handleSectionToggle = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const validateEndDate = (endDate: string): boolean => {
    if (!endDate) return true;
    const now = dayjs();
    const selectedEndDate = dayjs(endDate).endOf('day');
    return selectedEndDate.isBefore(now);
  };

  const handleInputChange = (field: keyof TaskFormData, value: any) => {

    if (isEditMode && isFieldDisabled(field)) {
      return;
    }

    if (field === 'thresholdValue') {
      const numericValue = value.replace(/[^\d.]/g, '');
      const decimalCount = (numericValue.match(/\./g) || []).length;
      if (decimalCount > 1) {
        return;
      }
      const parts = numericValue.split('.');
      if (parts.length > 1 && parts[1].length > 2) {
        return;
      }
      if (numericValue === '' || numericValue === '0' || numericValue === '0.' || numericValue === '.0') {
        setTaskData(prev => ({
          ...prev,
          [field]: ''
        }));
        return;
      }
      if (numericValue && !isNaN(Number(numericValue))) {
        const numValue = Number(numericValue);
        if (numValue > 999999999) {
          openPopup("Alert", "Threshold value cannot exceed 999,999,999");
          return;
        }
        if (numValue < 1) {
          openPopup("Alert", "Threshold value cannot be less than 1");
          return;
        }
      }
      setTaskData(prev => ({
        ...prev,
        [field]: numericValue
      }));
      return;
    }

    if (field === 'pointsReward') {
      const numericValue = value.replace(/[^\d]/g, '');
      if (numericValue && !isNaN(Number(numericValue))) {
        const numValue = Number(numericValue);
        if (numValue > 999999999) {
          openPopup("Alert", "Points reward cannot exceed 999,999,999");
          return;
        }
      }
      if (numericValue === '' || numericValue === '0') {
        setTaskData(prev => ({
          ...prev,
          [field]: ''
        }));
        return;
      }
      setTaskData(prev => ({
        ...prev,
        [field]: numericValue
      }));
      return;
    }

    if (field === 'guidanceType') {
      setTaskData(prev => ({
        ...prev,
        guidanceType: value,
        guidanceContent: ''
      }));
      return;
    }

    setTaskData(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === 'category') {
      setTaskData(prev => ({
        ...prev,
        category: value,
        template: ''
      }));
    }

    if (field === "applyToAll" && value) {
      setOutletBifurcationsList([]);
    }

    if (field === 'endDate') {
      if (validateEndDate(value)) {
        openPopup("Alert", "End date cannot be in the past. Please select today or a future date.");
        setTaskData(prev => ({ ...prev, endDate: '' }));
        return;
      }

      if (taskData.startDate) {
        const start = new Date(taskData.startDate);
        const end = new Date(value);

        if (end < start) {
          openPopup("Alert", "End date should be greater than start date.");
          setTaskData(prev => ({ ...prev, endDate: '' }));
          return;
        }
      }
    }

    if (field === 'startDate' && taskData.endDate) {
      const start = new Date(value);
      const end = new Date(taskData.endDate);

      if (end < start) {
        openPopup("Alert", "End date should be greater than start date.");
        setTaskData(prev => ({ ...prev, startDate: '' }));
      }
    }
  };

  const handleRedirectionChange = (value: string) => {
    if (isEditMode) {
      const selectedOption = navigationOptions.find(option => option.id === value);
      setSelectedRedirection(selectedOption);
      setTaskData(prev => ({
        ...prev,
        redirectionPlaceholder: value,
        redirection: selectedOption
      }));
    } else {
      const selectedOption = navigationOptions.find(option => option.id === value);
      setSelectedRedirection(selectedOption);
      setTaskData(prev => ({
        ...prev,
        redirectionPlaceholder: value
      }));
    }
  };

  const handleGuidanceContentChange = (value: string) => {
    if (isEditMode) return;
    setTaskData(prev => ({
      ...prev,
      guidanceContent: value
    }));
  };

  const getAllOutletOptions = async (
    combinedSelectedValues: combinedSelectedValuesType,
    currentTabName: string,
    name: string,
    searchText?: string
  ) => {
    setOutletLoading(true);
    try {
      const currTabPriority = getEntityPriority(currentTabName);
      const data: any = await getMarketDetailsOptions(combinedSelectedValues, currTabPriority, currentTabName, searchText, name);

      const newData: Record<string, Array<{ label: string; value: string }>> = {};
      for (let tabName in data) {
        const colname = DB_COLUMN_NAME_MAP_COLUMN_NAME[tabName] ?? tabName;
        newData[colname] = data[tabName];
      }

      return newData;
    } catch (err) {
      console.error(err);
      return {};
    } finally {
      setOutletLoading(false);
    }
  };

  const getSelectedModule = (): ActiveModule | undefined => {
    return activeModules.find(module => normalizeText(module.taskName) === normalizeText(taskData.category));
  };

  const generatePreview = (): string => {
    if (taskData.description && taskData.description.trim() !== '') {
      const customDescription = taskData.description.trim();
      if (customDescription.includes('X')) {
        return customDescription.replace(/X/g, taskData.thresholdValue || '0');
      }
      return customDescription;
    }
    const template = taskData.template;
    if (!template || !taskData.thresholdValue) return template || '';
    return template.replace(/X/g, taskData.thresholdValue || '0');
  };

  const getConfigTaskName = (): string => {
    const selectedModule = getSelectedModule();
    return selectedModule?.taskName || taskData.category.toUpperCase();
  };

  const getTargetType = (): string => {
    const selectedModule = getSelectedModule();
    if (!selectedModule) return '';
    const matchingTask = selectedModule.tasks.find(
      task => task.subtext === taskData.template
    );

    return matchingTask?.targetType || '';
  };

  const formatDate = (dateString: string, time: string): string => {
    if (!dateString) return '';
    return `${dateString} ${time}`;
  };

  const calculateMonetaryValue = (): string => {
    if (!taskData.thresholdValue || !taskData.pointsReward || !taskData.template || !redemptionConfig) {
      return '';
    }

    const points = parseInt(taskData.pointsReward || '0');
    const unitConversion = redemptionConfig.unitConversion || 0;
    const monetaryValue = formatDecimal(points * unitConversion);

    return `${currency} ${monetaryValue}`;
  };

  const handleCreateTask = async () => {
    if (isSubmitting) return;
    
    if (isEditMode) {
      const taskStatus = getTaskStatus();
      let hasChanges = false;

      if (taskStatus === 'not-started') {
        hasChanges = taskData.startDate !== locationState.taskData.startDate ||
          taskData.endDate !== locationState.taskData.endDate;
      } else if (taskStatus === 'active') {
        hasChanges = taskData.endDate !== locationState.taskData.endDate;
      }
      if (!hasChanges) {
        navigate(-1);
        return;
      }
    }
    
    setIsSubmitting(true);
    
    try {
      const extendedAttributes: any = {
        guidanceType: taskData.guidanceType === 'video' ? 'video' : 'written',
        guidanceContent: taskData.guidanceContent,
        template: taskData.template,
        bannerLink: bannerLink,
        preview: generatePreview(),
        ...(taskData.applyToAll
          ? {}
          : {
            outletBifurcationsList: outletBifurcationsList.map((item) => ({
              id: item.id,
              distributorDetails: item.distributorDetails || [],
              channel: item.channel || [],
              outletClass: item.outletClass || [],
              outletCode: item.outletCode || []
            }))
          })
      };

      if (!hasRedirectionRoute() && selectedRedirection) {
        extendedAttributes.redirection = selectedRedirection;
      }

      const DELIMITER = '||';

      const outletAttributes = taskData.applyToAll
        ? [
          {
            outletcode: null,
            outletClass: null,
            channel: null,
            distributor: null,
          },
        ]
        : outletBifurcationsList.map((item) => ({
          outletcode: (item.outletCode || []).join(DELIMITER) || null,
          outletClass: (item.outletClass || []).join(DELIMITER) || null,
          distributor: (item.distributorDetails || []).join(DELIMITER) || null,
          channel: (item.channel || []).join(DELIMITER) || null,
        }));

      const requestBody: any = {
        action: "LOYALTY",
        activeStatus: taskData.activeStatus,
        extendedAttributes: extendedAttributes,
        targetType: getTargetType(),
        taskName: getConfigTaskName(),
        displayName: taskData.taskName,
        expiry: null,
        description: taskData.description,
        points: parseInt(taskData.pointsReward) || 0,
        threshold: parseInt(taskData.thresholdValue) || 0,
        startTime: formatDate(taskData.startDate, "00:00:00"),
        endTime: formatDate(taskData.endDate, "23:59:59"),
        outletAttributes
      };
      if (isEditMode && locationState?.taskId) {
        requestBody.id = locationState.taskId;
      }

      let createTaskResponse;

      if (isEditMode) {
        await updateTask(requestBody);
        openPopup('Alert', 'Task updated successfully!');
      } else {
        createTaskResponse = await createTask(requestBody);

        openPopup('Alert', `Task created successfully!`);
        if (taskData.category === 'IRED' && createTaskResponse?.data?.ruleId) {
          handleCreateCampaign(createTaskResponse.data.ruleId);
        }
      }
      navigate(-1);
    } catch (error) {
      console.error(isEditMode ? 'Error updating task:' : 'Error creating task:', error);
      openPopup(
        'Alert',
        isEditMode ? 'Error updating task. Please try again.' : 'Error creating task. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateFile = (file: File): boolean => {
    if (file.size > MAX_FILE_SIZE) {
      openPopup('Alert', 'File size should not exceed 5MB');
      return false;
    }
    return true;
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!validateFile(file)) {
        event.target.value = '';
        return;
      }

      setSelectedFile(file);
      const tempPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(tempPreviewUrl);
      try {
        const uploadedBannerLink = await uploadFile(file);
        setBannerLink(uploadedBannerLink);
        openPopup('Success', 'Banner image uploaded successfully!');
      } catch (error) {
        console.error('Error uploading banner image:', error);
        openPopup('Alert', 'Error uploading banner image. Please try again.');
        setSelectedFile(null);
        setPreviewUrl('');
        event.target.value = '';
      }
    }
  };

  const uploadFile = async (file: File): Promise<string> => {
    const uploadFormData = new FormData();
    uploadFormData.append("files", file, file.name);

    const fileUploadResponse = await uploadDocument(uploadFormData);
    const blobKey: string = JSON.parse(fileUploadResponse[0].body).features[0].Uri;

    return blobKey;
  };

  const handleRemoveImage = () => {
    if (previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl('');
    setBannerLink('');

    const fileInput = document.getElementById('banner-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleCreateCampaign = async (taskId: string) => {
    try {
      const DELIMITER = '||';

      const campaignOutletAttributes = taskData.applyToAll
        ? [
          {
            channel: null,
            outletClass: null,
            outletType: null,
            distributor: null,
            outletcode: null,
          },
        ]
        : outletBifurcationsList.map((item) => ({
          channel: (item.channel || []).join(DELIMITER) || null,
          outletClass: (item.outletClass || []).join(DELIMITER) || null,
          outletType: null,
          distributor:
            (item.distributorDetails || []).join(DELIMITER) || null,
          outletcode: (item.outletCode || []).join(DELIMITER) || null,
        }));

      const campaignBody = {
        bannerLink: bannerLink,
        startDate: formatDate(taskData.startDate, "00:00:00"),
        endDate: formatDate(taskData.endDate, "23:59:59"),
        sceneId: iredScene.sceneId,
        sceneType: iredScene.sceneType,
        subSceneId: iredScene.subSceneId,
        subSceneType: iredScene.subSceneType,
        taskDescription: generatePreview(),
        taskId: taskId,
        outletAttributes: campaignOutletAttributes
      };
      const campaignResponse = await createCampaign(campaignBody);
    } catch (error) {
      console.error('Error creating campaign:', error);
    }
  };

  const isFormValid = (): boolean => {
    const hasOutletSelection =
      taskData.applyToAll ||
      outletBifurcationsList.some(
        (item) =>
          (item.outletCode || []).length > 0 ||
          (item.outletClass || []).length > 0 ||
          (item.channel || []).length > 0 ||
          (item.distributorDetails || []).length > 0
      );

    const isBannerValid = taskData.category !== 'IRED' || (taskData.category === 'IRED' && bannerLink);
    const isRedirectionValid = hasRedirectionRoute() ? true : !!taskData.redirectionPlaceholder;

    return !!(
      taskData.taskName?.trim() &&
      isRedirectionValid &&
      taskData.category &&
      taskData.template &&
      taskData.thresholdValue &&
      taskData.pointsReward &&
      taskData.startDate &&
      taskData.endDate &&
      taskData.guidanceContent?.trim() &&
      hasOutletSelection &&
      isBannerValid
    );
  };

  return (
    <div className="createTask-wrapper">
      {showLoader ? (
        <div className="loader-center">
          {/* <Loader /> */}
        </div>
      ) : (
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#344257' }}>
              Task Creation Portal
            </Typography>

            <Button
              variant="contained"
              onClick={() => navigate(-1)}
              sx={{
                padding: '4px 12px',
                border: 'none',
                borderRadius: '3px',
                color: 'white',
                fontWeight: 700,
                width: '70px',
                height: '32px',
                backgroundColor: '#808080',
                boxShadow: 'none',
                textTransform: 'none',
                fontSize: '0.75rem',
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: '#707070',
                  boxShadow: 'none',
                },
                '&:active': {
                  backgroundColor: '#606060',
                },
              }}
            >
              Back
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create and configure loyalty program tasks for your retailers
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {/* 1. Task Setup & Templates */}
                <Accordion
                  expanded={expandedSections.taskSetup}
                  onChange={() => handleSectionToggle('taskSetup')}
                  sx={accordionStyle}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      ...accordionSummaryStyle,
                      minHeight: '30px',
                      '& .MuiAccordionSummary-content': {
                        margin: '8px 0',
                      }
                    }}
                  >
                    <Header title={"1. Task Setup & Templates"} />
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={1.5} alignItems="flex-start" justifyContent="space-between">
                      {/* Task Name */}
                      <Grid item xs={12} sm={5}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                          Task Name<span style={{ color: "#d32f2f" }}>*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          value={taskData.taskName}
                          inputProps={{ maxLength: 100 }}
                          onChange={(e) => handleInputChange('taskName', e.target.value)}
                          disabled={isFieldDisabled('taskName')}
                          placeholder="Enter task name"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              height: '32px',
                              fontSize: '0.875rem'
                            }
                          }}
                        />
                      </Grid>

                      {/* Category */}
                      <Grid item xs={12} sm={5}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                          Category<span style={{ color: "#d32f2f" }}>*</span>
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={taskData.category}
                            onChange={(e) => handleInputChange('category', e.target.value)}
                            displayEmpty
                            disabled={isFieldDisabled('category')}
                            renderValue={(selected) => {
                              if (!selected) {
                                return <span style={{ color: '#999', fontSize: '0.875rem' }}>Select category</span>;
                              }
                              return selected;
                            }}
                            sx={{
                              height: '32px',
                              fontSize: '0.875rem',
                              '& .MuiSelect-select': {
                                paddingTop: '6px',
                                paddingBottom: '6px'
                              }
                            }}
                          >
                            {activeModules.map((module) => (
                              <MenuItem key={module.taskName} value={module.taskName} sx={{ fontSize: '0.875rem' }}>
                                {module.taskName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      {/* Template */}
                      {taskData.category && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                            Template<span style={{ color: "#d32f2f" }}>*</span>
                          </Typography>
                          <FormControl fullWidth size="small">
                            <Select
                              value={taskData.template}
                              onChange={(e) => handleInputChange('template', e.target.value)}
                              disabled={isFieldDisabled('template')}
                              renderValue={(selected) => {
                                if (!selected) {
                                  return <span style={{ color: '#999', fontSize: '0.875rem' }}>Select Template</span>;
                                }
                                return selected;
                              }}
                              sx={{
                                height: '32px',
                                fontSize: '0.875rem',
                                '& .MuiSelect-select': {
                                  paddingTop: '6px',
                                  paddingBottom: '6px'
                                }
                              }}
                            >
                              {getSelectedModule()?.tasks.map((task) => (
                                <MenuItem key={task.taskIndex} value={task.subtext} sx={{ fontSize: '0.875rem' }}>
                                  {task.subtext}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}

                      {/* Description */}
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                          Display Task Description
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={1}
                          inputProps={{ maxLength: 200 }}
                          value={taskData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          placeholder="Describe the task requirements..."
                          disabled={isFieldDisabled('description')}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '0.875rem'
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* 2. Thresholds, Timing & Rewards */}
                <Accordion
                  expanded={expandedSections.thresholds}
                  onChange={() => handleSectionToggle('thresholds')}
                  sx={accordionStyle}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      ...accordionSummaryStyle,
                      minHeight: '30px',
                      '& .MuiAccordionSummary-content': {
                        margin: '8px 0',
                      }
                    }}
                  >
                    <Header title={"2. Thresholds, Timing & Rewards"} />
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {taskData.template && (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            bgcolor: '#FDFDFD',
                            borderRadius: '6px',
                            borderColor: "#F4F5F6"
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 550, color: '#344257', fontSize: '0.8rem' }}>
                              Selected Template
                            </Typography>
                            {taskData.category && (
                              <Chip
                                label={taskData.category}
                                size="small"
                                sx={{
                                  fontWeight: 550,
                                  fontSize: '0.75rem',
                                  bgcolor: '#F4F5F6',
                                  color: '#344257',
                                  height: '20px'
                                }}
                              />
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ mt: 0.5, color: "#5C6777", fontSize: '0.8rem' }}>
                            {taskData.template}
                          </Typography>
                        </Paper>
                      )}

                      {/* Tabs */}
                      <Box>
                        <Tabs
                          value={activeRuleTab}
                          onChange={(e, value) => setActiveRuleTab(value)}
                          variant="fullWidth"
                          sx={{
                            backgroundColor: '#E7ECEF',
                            borderRadius: 1,
                            minHeight: '28px',
                            '& .MuiTab-root': {
                              textTransform: 'none',
                              fontWeight: 500,
                              color: '#627984',
                              fontSize: '0.85rem',
                              borderRadius: 1,
                              minHeight: '28px',
                              margin: '2px',
                              padding: '4px 8px',
                              '&.Mui-selected': {
                                backgroundColor: '#fff',
                                color: '#000',
                                fontWeight: 600,
                              },
                            },
                            '& .MuiTabs-flexContainer': {
                              gap: '0px',
                            },
                            '& .MuiTabs-indicator': {
                              display: 'none',
                            },
                          }}
                        >
                          <Tab label="Fixed Rules" disableRipple />
                        </Tabs>
                      </Box>

                      {/* Threshold Configuration */}
                      {activeRuleTab === 0 && (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            bgcolor: '#FDFDFD',
                            borderRadius: '6px',
                            borderColor: "#F4F5F6"
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 550, color: '#344257', fontSize: '0.8rem' }}>
                            Configure Template Values
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.25, color: "#5C6777", fontSize: '0.75rem' }}>
                            Replace the 'X' values in your template with specific thresholds
                          </Typography>

                          <Grid container spacing={1.5} sx={{ mt: 0.5, alignItems: 'flex-start' }}>
                            {/* Threshold Value */}
                            <Grid item xs={12} sm={5}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, mb: 0.25, fontSize: '0.8rem' }}
                              >
                                Threshold Value<span style={{ color: "#d32f2f" }}>*</span>
                              </Typography>
                              <TextField
                                fullWidth
                                type="number"
                                placeholder="Enter threshold value"
                                value={taskData.thresholdValue}
                                onChange={(e) => handleInputChange('thresholdValue', e.target.value)}
                                onKeyDown={(e) => {
                                  if (['-', 'e', 'E'].includes(e.key)) e.preventDefault();
                                }}
                                inputProps={{ min: "0", pattern: "[0-9]*" }}
                                size="small"
                                disabled={isFieldDisabled('thresholdValue')}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    height: '32px',
                                    fontSize: '0.875rem'
                                  }
                                }}
                              />
                            </Grid>

                            {/* Points Reward */}
                            <Grid
                              item
                              xs={12}
                              sm={5}
                              sx={{
                                marginLeft: 'auto',
                                textAlign: 'left'
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, mb: 0.25, fontSize: '0.8rem' }}
                              >
                                Points Reward<span style={{ color: "#d32f2f" }}>*</span>
                              </Typography>
                              <TextField
                                fullWidth
                                type="number"
                                placeholder="Points to award"
                                value={taskData.pointsReward}
                                onChange={(e) => handleInputChange('pointsReward', e.target.value)}
                                onKeyDown={(e) => {
                                  if (['.', '-', 'e', 'E'].includes(e.key)) e.preventDefault();
                                }}
                                inputProps={{ min: "0", pattern: "[0-9]*" }}
                                size="small"
                                disabled={isFieldDisabled('pointsReward')}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    height: '32px',
                                    fontSize: '0.875rem'
                                  }
                                }}
                              />
                            </Grid>
                          </Grid>

                          {taskData.thresholdValue && (
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1,
                                mt: 1,
                                bgcolor: '#F1F7FA',
                                borderRadius: '4px',
                                border: "none",
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                                  Preview:
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {generatePreview()}
                                </Typography>
                              </Box>
                            </Paper>
                          )}

                          {(taskData.thresholdValue && taskData.pointsReward && taskData.template) && (
                            <Typography
                              variant="caption"
                              sx={{ color: '#909090', mt: 0.5, display: 'block', fontSize: '0.7rem' }}
                            >
                              ≈ {calculateMonetaryValue()} value
                            </Typography>
                          )}
                        </Paper>
                      )}

                      <Divider sx={{ my: 1 }} />

                      {/* Additional Settings */}
                      {(!hasRedirectionRoute() || taskData.category === 'IRED') &&
                        <Typography variant="body2" sx={{ fontWeight: 550, fontSize: "0.9rem", color: '#344257' }}>
                          Additional Settings
                        </Typography>
                      }
                      {!hasRedirectionRoute() && (
                        <>
                          <Box>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                              Redirection Placeholder<span style={{ color: "#d32f2f" }}>*</span>
                            </Typography>
                            <FormControl fullWidth size="small" sx={{ width: '41%' }}>
                              <Select
                                value={taskData.redirectionPlaceholder}
                                onChange={(e) => handleRedirectionChange(e.target.value)}
                                displayEmpty
                                disabled={isFieldDisabled('redirectionPlaceholder')}
                                renderValue={(selected) => {
                                  if (!selected) {
                                    return <span style={{ color: '#999' }}>Select redirection option</span>;
                                  }
                                  const selectedOption = navigationOptions.find(option => option.id === selected);
                                  return selectedOption ? selectedOption.label : selected;
                                }}
                                sx={{
                                  height: '32px',
                                  fontSize: '0.875rem',
                                  '& .MuiSelect-select': {
                                    paddingTop: '6px',
                                    paddingBottom: '6px'
                                  }
                                }}
                              >
                                {navigationOptions.map((option) => (
                                  <MenuItem key={option.id} value={option.id} sx={{ fontSize: '0.875rem' }}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <FormHelperText>URL to redirect users after task completion</FormHelperText>
                          </Box>
                        </>
                      )}
                      {taskData.category === 'IRED' && (
                        <Box sx={{ mt: hasRedirectionRoute() ? 0 : 0.5 }}>
                          <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                            Banner Image<span style={{ color: "#d32f2f" }}>*</span>
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {/* Image Preview */}
                            <Box
                              sx={{
                                width: 50,
                                height: 50,
                                border: '1.5px dashed #ddd',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#fafafa',
                                position: 'relative'
                              }}
                            >
                              {previewUrl ? (
                                <>
                                  <img
                                    src={previewUrl}
                                    alt="Campaign banner"
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      borderRadius: '6px'
                                    }}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={handleRemoveImage}
                                    disabled={isEditMode}
                                    sx={{
                                      position: 'absolute',
                                      top: -8,
                                      right: -8,
                                      backgroundColor: 'white',
                                      boxShadow: 1,
                                      '&:hover': {
                                        backgroundColor: 'grey.100'
                                      }
                                    }}
                                  >
                                    <CloseIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </>
                              ) : (
                                <ImageIcon sx={{ color: '#ccc', fontSize: 24 }} />
                              )}
                            </Box>

                            {/* Upload Button */}
                            <Box>
                              <input
                                accept={ACCEPTED_FILE_TYPES}
                                style={{ display: 'none' }}
                                id="banner-upload"
                                type="file"
                                onChange={handleFileUpload}
                                disabled={isEditMode}
                              />
                              <label htmlFor="banner-upload">
                                <Button
                                  component="span"
                                  variant="outlined"
                                  size="small"
                                  startIcon={<UploadIcon />}
                                  disabled={isEditMode}
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    borderRadius: '6px',
                                    width: '280px',
                                  }}
                                >
                                  Upload Image
                                </Button>
                              </label>
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                                Max 5MB • PNG, JPG
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      )}
                      {!hasRedirectionRoute() && <Divider sx={{ my: 1 }} />}

                      {/* Schedule & Timing */}
                      <Typography variant="body2" sx={{ fontWeight: 550, fontSize: "0.9rem", color: '#344257' }}>
                        Schedule
                      </Typography>

                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Grid container spacing={1.5} alignItems="flex-start">
                          {/* Start Date */}
                          <Grid item xs={12} sm={5}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                              Start Date<span style={{ color: "#d32f2f" }}>*</span>
                            </Typography>
                            <DatePicker
                              inputFormat="DD/MM/YYYY"
                              value={taskData.startDate ? dayjs(taskData.startDate) : null}
                              onChange={(newValue: Dayjs | null) => {
                                handleInputChange('startDate', newValue ? newValue.format('YYYY-MM-DD') : '');
                              }}
                              disabled={isFieldDisabled('startDate')}
                              renderInput={(params) => (
                                <TextField
                                  fullWidth
                                  size="small"
                                  {...params}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      height: '32px'
                                    }
                                  }}
                                />
                              )}
                            />
                          </Grid>

                          {/* End Date */}
                          <Grid item xs={12} sm={5} sx={{ marginLeft: 'auto', textAlign: 'left' }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                              End Date<span style={{ color: "#d32f2f" }}>*</span>
                              {isEditMode && isFieldDisabled('endDate') && getTaskStatus() === 'expired' && (
                                <span style={{ color: "#d32f2f", marginLeft: '4px', fontSize: '0.7rem' }}>Expired task can't be edited</span>
                              )}
                            </Typography>
                            <DatePicker
                              inputFormat="DD/MM/YYYY"
                              value={taskData.endDate ? dayjs(taskData.endDate) : null}
                              onChange={(newValue: Dayjs | null) => {
                                handleInputChange('endDate', newValue ? newValue.format('YYYY-MM-DD') : '');
                              }}
                              disabled={isFieldDisabled('endDate')}
                              renderInput={(params) => (
                                <TextField
                                  fullWidth
                                  size="small"
                                  {...params}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      height: '32px'
                                    }
                                  }}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </LocalizationProvider>
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* 3. Assignment & Targeting */}
                <Accordion
                  expanded={expandedSections.assignment}
                  onChange={() => handleSectionToggle('assignment')}
                  sx={accordionStyle}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      ...accordionSummaryStyle,
                      minHeight: '30px',
                      '& .MuiAccordionSummary-content': {
                        margin: '8px 0',
                      }
                    }}
                  >
                    <Header title={"3. Assignment & Targeting"} />
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 1, pb: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          bgcolor: '#FDFDFD',
                          borderRadius: '6px',
                          borderColor: "#F4F5F6"
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 550, color: '#344257', fontSize: '0.8rem' }}>
                            Apply to All Retailers
                          </Typography>
                          <Switch
                            checked={taskData.applyToAll}
                            onChange={(e) => handleInputChange('applyToAll', e.target.checked)}
                            disabled={isEditMode}
                            size="small"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#24c6b1',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#24c6b1',
                              },
                              '& .MuiSwitch-track': {
                                backgroundColor: '#909090',
                              },
                            }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ mt: 0.25, color: "#5C6777", fontSize: '0.75rem' }}>
                          Assign this task to all retailers across all markets
                        </Typography>
                      </Paper>
                      {!taskData.applyToAll && (
                        <div className="outlet-details-wrapper">
                          <div className="market-details-wrapper-header">
                            <Button
                              variant="contained"
                              className="outlet-details-save-btn"
                              size="small"
                              disabled={isEditMode}
                              startIcon={<DoneIcon />}
                              onClick={handleSaveOutlet}
                            >
                              {editId ? "Update" : "Save"}
                            </Button>
                          </div>
                          <div className="market-detials-wrapper-content">
                            <div className='filter-wrapper' style={{ paddingBlock: '0px' }}>
                              <OutletFilters
                                handleChange={handleOutletChangeWrapper}
                                editMode={!!editId}
                                readOnly={isEditMode}
                                values={{
                                  schemeOutletBifurcations: outletValues.schemeOutletBifurcations,
                                  schemeLocationBifurcations: {},
                                }}
                                getOutletOptions={getAllOutletOptions}
                                loading={outletLoading}
                                outletCurrentTab={outletCurrentTab}
                                setOutletCurrentTab={setOutletCurrentTab}
                                outletLevelTabsList={outletLevelTabsList}
                                multiple={true}
                                arbitraryList={[]}
                                chipLimit={50}
                                dataLimit={100}
                              />
                            </div>
                            <SelectedOutletDetails
                              handleRowEdit={handleEditButtonClick}
                              handleRowDelete={handleRowDelete}
                              editingRow={editId !== null}
                              tabsList={outletLevelTabsList}
                              outletBifurcationsList={outletBifurcationsList}
                              isEditMode={isEditMode}
                            />
                          </div>
                        </div>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* 4. Guidance & Instructions */}
                <Accordion
                  expanded={expandedSections.guidance}
                  onChange={() => handleSectionToggle('guidance')}
                  sx={accordionStyle}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      ...accordionSummaryStyle,
                      minHeight: '30px',
                      '& .MuiAccordionSummary-content': {
                        margin: '8px 0',
                      }
                    }}
                  >
                    <Header title={"4. Guidance & Instructions"} />
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 1, pb: 2 }}>
                    {/* <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                          Choose Guidance Type
                        </Typography>
                        <FormControl component="fieldset">
                          <RadioGroup
                            row
                            value={taskData.guidanceType}
                            onChange={(e) => handleInputChange('guidanceType', e.target.value as 'written' | 'video')}
                            sx={{ gap: 2 }}
                          >
                            <FormControlLabel
                              value="written"
                              control={<Radio disabled={isEditMode} size="small" />}
                              label={<Typography variant="subtitle2" sx={{ fontSize: '0.8rem' }}>Written Steps</Typography>}
                            />
                            <FormControlLabel
                              value="video"
                              control={<Radio disabled={isEditMode} size="small" />}
                              label={<Typography variant="subtitle2" sx={{ fontSize: '0.8rem' }}>Video Guide</Typography>}
                            />
                          </RadioGroup>
                        </FormControl>
                      </Box> */}
                    {taskData.guidanceType === 'written' && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                          Guiding Steps for Retailers<span style={{ color: "#d32f2f" }}>*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          value={taskData.guidanceContent || ''}
                          onChange={(e) => handleGuidanceContentChange(e.target.value)}
                          placeholder="Write step-by-step instructions to help retailers complete this task..."
                          disabled={isEditMode}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '0.875rem'
                            }
                          }}
                        />
                      </Box>
                    )}

                    {/* {taskData.guidanceType === 'video' && (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                            Guidance Video
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            value={taskData.guidanceContent || ''}
                            onChange={(e) => handleGuidanceContentChange(e.target.value)}
                            placeholder="YouTube/Vimeo URL"
                            disabled={isEditMode}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: '32px',
                                fontSize: '0.875rem'
                              }
                            }}
                          />
                        </Box>
                      )} */}
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Grid>

            {/* Summary Card */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  position: 'sticky',
                  top: 16,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Description style={{ color: "#344257", fontSize: '18px' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: "#344257", fontSize: '1rem' }}>
                      Summary
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Category:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{taskData.category || '-'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Points:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{taskData.pointsReward || '0'} points</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Threshold:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{taskData.thresholdValue || '0'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Amount:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{calculateMonetaryValue() || '0'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Start Date:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{taskData.startDate ? dayjs(taskData.startDate).format('DD-MM-YYYY') : '-'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>End Date:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{taskData.endDate ? dayjs(taskData.endDate).format('DD-MM-YYYY') : '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                <Box sx={{ px: 1.5, py: 1, mt: 1, bgcolor: '#FFF9C4', borderRadius: 3 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'flex',
                      gap: 0.5,
                      lineHeight: 1.3,
                      fontSize: '0.7rem'
                    }}
                  >
                    <Info sx={{ fontSize: "14px", color: "#F29C38", mt: "1px" }} />
                    There is no priority to task. If the task is created twice for the same outlet, it will mean that the tasks overlap.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button variant="outlined" onClick={() => navigate(-1)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateTask}
              disabled={!isFormValid() || isSubmitting}
              sx={{ bgcolor: '#3BCDB7', '&:hover': { bgcolor: '#57d6c3' } }}
            >
              {isEditMode ? "Edit Task" : "Create Task"}
            </Button>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default CreateTask;